import React, { useState, useEffect, useRef } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FormattedDate } from 'react-intl'
import { Button, Spinner, FadeIn } from 'lib/ui/components'
import { useProductSelectorResults } from 'hooks/productSelector'
import { FeaturedAttributes } from 'app/sofia/components/FeaturedAttributes'
import { SofiaWrapper } from 'components/ProductCard/styled'
import { ModalExtraWrapper } from 'scenes/Product/components/OptionsSection/styled'
import { AddToCart } from './components/AddToCart'
import { NoResults } from './components/NoResults'
import {
  Header,
  Section,
  Container,
  Product,
  FeaturedWrapper,
  OptionsWrapper
} from './styled'
import { Layout } from 'components/ProductSelector'
import { SubscriptionOptions } from './components/SubscriptionOptions'
import { getProductAttributeList } from 'lib/core/data/common/productAttributeList/selectors'
import { normalizeProductCustomAttributes, AddToCartModal } from 'lib/modules/catalog'
import { useCustomGTM } from 'hooks/GTM'
import { getPackQty } from 'scenes/Product/components/OptionsSection'
import { PerfectProductModal } from './components/PerfectProductModal'
import { MediaGallery } from 'scenes/Product/components/MediaGallery'
import { ReactComponent as ArrowRight } from 'resources/svg/icons/arrow-right.svg'
import { TopBlock } from './components/TopBlock'
import { BottomBlock } from './components/BottomBlock'
import { useCmsBlock } from 'lib/core/hooks'
import { CMS_BLOCK } from 'app/sofia/resources/constants'

export const PROMO_SUB_FREQUENCY = 2

export const Results = () => {
  const history = useHistory()
  const extraProductsRef = useRef<HTMLDivElement>(null)
  const primaryProductRef = useRef<HTMLDivElement>(null)
  const availableOptions = useSelector(getProductAttributeList) || {}
  const staticBlock = useCmsBlock({
    id: CMS_BLOCK.PPQ_ADD_TO_MODAL,
    isLazy: false
  })

  const {
    primaryProduct,
    sku,
    usesByDay,
    hasResults,
    promotionValue,
    productSku,
    setProductSku,
    productUrl,
    extraProducts
  } = useProductSelectorResults() ?? {}
  const { triggerQuizResult } = useCustomGTM()
  const [shipmentFrequency, setShipmentFrequency] = useState(4)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(true)
  const [mediaGalleryIndex, setMediaGalleryIndex] = useState(0)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [packsToAdd, setPacksToAdd] = useState(parseInt(usesByDay))

  useEffect(() => {
    if (sku && primaryProduct && promotionValue) {
      triggerQuizResult({
        sku: productSku,
        brand,
        bladderLeakageLevel: absorbency
      })
    }
    // eslint-disable-next-line
  }, [sku, primaryProduct])

  if (!sku && !primaryProduct) {
    history.push('/product-selector')
    return null
  }

  if (!hasResults) {
    return <NoResults />
  }

  if (!primaryProduct || !promotionValue) {
    return <Spinner />
  }

  const {
    id,
    price_range: price,
    name,
    image,
    visiture_custom_attributes: visitureCustomAttributes,
    media_gallery_entries: mediaGalleryEntries
  } = primaryProduct ?? {}

  const { customAttributesIndex } = normalizeProductCustomAttributes(visitureCustomAttributes, availableOptions)

  const imageUrl = image?.url
  const [absorbency] = customAttributesIndex?.primary_bladder_leakage ?? []
  const [discreet] = customAttributesIndex?.discreet ?? []
  const [overnight] = customAttributesIndex?.overnight ?? []
  const [innerLegCuff] = customAttributesIndex?.innerLegCuff ?? []
  const [packageQty] = customAttributesIndex?.package_qty ?? []
  const [brand] = customAttributesIndex?.manufacturer ?? []
  const availableFrequencies = customAttributesIndex.available_frequencies
  const quantityPerDay = parseInt(usesByDay)

  const piecesQty = parseInt(packageQty)
  const packs = (getPackQty?.(piecesQty, quantityPerDay, shipmentFrequency)) ?? 0
  const promotionalPacks = getPackQty?.(piecesQty, quantityPerDay, PROMO_SUB_FREQUENCY)
  const promoSubTotalPieces = promotionalPacks * piecesQty

  const scrollToRef = (ref: any) => {
    const top = (ref?.current?.offsetTop)
    window.scrollTo({
      left: 0,
      top,
      behavior: 'smooth'
    })
  }

  const handleProductSelect = (sku: string) => {
    setProductSku(sku)
    scrollToRef(primaryProductRef)
  }

  const deliveryDate = new Date()
  deliveryDate.setDate((deliveryDate.getDate() + 14))

  return (
    <Layout>
      <>
        <FadeIn>
          <>
            <AddToCartModal
              isModalVisible={showSuccessModal}
              setIsModalVisible={setShowSuccessModal}
              productName={name}
              productImage={imageUrl}
              sku={productSku}
              continueShoppingLink='/bladder-control'
            >
              <ModalExtraWrapper>
                {shipmentFrequency > 0 && (
                  <div>
                    {staticBlock.content && promotionValue && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: staticBlock.content
                        }}
                      />
                    )}
                    <p>Ships every {shipmentFrequency} weeks</p>
                  </div>
                )}
              </ModalExtraWrapper>
            </AddToCartModal>
            <PerfectProductModal
              isOpen={isSuccessModalOpen}
              setModalOpen={setIsSuccessModalOpen}
              promoSubscriptionFrequency={PROMO_SUB_FREQUENCY}
              promotionValue={promotionValue}
              promotionalPacks={promotionalPacks}
              promoSubTotalPieces={promoSubTotalPieces}
              productImage={imageUrl}
            />
            <Section className='top'>
              <Container>
                <Product ref={primaryProductRef}>
                  <div className='side'>
                    <h1 className='product__name'>{name}</h1>
                    <MediaGallery productImage={imageUrl} mediaGalleryItems={mediaGalleryEntries} mediaGalleryIndex={mediaGalleryIndex} setMediaGalleryIndex={setMediaGalleryIndex} withDots />
                  </div>
                  <div className='content'>
                    <div className='info-section _middle'>
                      <TopBlock
                        promotionValue={promotionValue}
                        promotionalPacks={promotionalPacks}
                        promoSubTotalPieces={promoSubTotalPieces}
                      />
                      <OptionsWrapper>
                        <SubscriptionOptions
                          productId={id}
                          frequencies={availableFrequencies}
                          productPrice={price}
                          shipmentFrequency={shipmentFrequency}
                          setShipmentFrequency={setShipmentFrequency}
                          packageQty={packageQty}
                          setPacksToAdd={setPacksToAdd}
                          quantityPerDay={quantityPerDay}
                        />
                      </OptionsWrapper>
                      <h4>Your subscription begins <FormattedDate value={deliveryDate} month='2-digit' day='2-digit' />. <span>FREE shipping over $40!</span></h4>
                      <p><small>Change or cancel anytime.</small></p>
                      <hr />
                      <div>
                        <div className='add-to'>
                          <AddToCart
                            isPromotion
                            sku={productSku}
                            qty={packs}
                            shipmentFrequency={shipmentFrequency}
                            quantityPerDay={quantityPerDay}
                            onSuccess={setShowSuccessModal}
                          />
                        </div>
                        <Link to={`/${productUrl}#fromProductSelector` ?? ''} className='btn-link'>
                          <span>Click here to purchase one time- no extra savings but you still get a free gift!</span>
                        </Link>
                      </div>
                      <hr />
                      <FeaturedWrapper>
                        <h3>We love this product because of…</h3>
                        <FeaturedAttributes
                          absorbency={absorbency}
                          discreet={discreet}
                          overnight={overnight}
                          innerLegCuff={innerLegCuff}
                        />
                      </FeaturedWrapper>
                      <hr />
                    </div>
                    <BottomBlock />
                  </div>
                </Product>
              </Container>
            </Section>
            {extraProducts && extraProducts?.length > 0 && (
              <Section className='middle' ref={extraProductsRef}>
                <Container>
                  <Header>
                    <h2>Here are some other selections you might like.</h2>
                  </Header>
                  <div className='related-products'>
                    {extraProducts?.slice(0, 2).map((product: any) => {
                      const { name, thumbnail, sku } = product
                      return (
                        <SofiaWrapper key={sku}>
                          <img src={thumbnail} alt={name} />
                          <span className='product-name'>{name}</span>
                          <Button
                            onClick={() => handleProductSelect(sku)}
                          >
                            Try it on!
                          </Button>
                        </SofiaWrapper>
                      )
                    })}
                  </div>
                </Container>
              </Section>
            )}
            <Section className='bottom'>
              <Container>
                <Header>
                  <h2>Or… Take a look for yourself.</h2>
                  <Link to='/bladder-control' className='btn-link'>
                    Shop all products
                    <ArrowRight />
                  </Link>
                </Header>
              </Container>
            </Section>
          </>
        </FadeIn>
      </>
    </Layout>
  )
}
